import { get, pick, uniq } from 'es-toolkit/compat';
import { useMemoCompare } from 'hooks/useMemoCompare';
import { useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useForm } from 'react-final-form';
import type { PathToFieldValueMap } from './form-spy.types';

type UseSpyOnMultipleFieldValuesProps = {
  fieldPaths: string[];
  comparisonFunc?: (a: unknown, b: unknown) => boolean;
};

export const useSpyOnMultipleFieldValues = ({
  fieldPaths,
  comparisonFunc = isEqual,
}: UseSpyOnMultipleFieldValuesProps): PathToFieldValueMap => {
  // We really really want to avoid re-registering a bunch of fields,
  // hard-core memoize these functions incase calling code is not careful
  const uniqueFieldPaths = useMemo(
    () => uniq(fieldPaths),
    [
      fieldPaths,
    ],
  );
  const memoizedPaths = useMemoCompare(uniqueFieldPaths);
  const stableCompareFunction = useMemoCompare(comparisonFunc);

  const formApi = useForm();
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const initialState = useMemo(() => {
    const initVals: PathToFieldValueMap = memoizedPaths.reduce((accMap, path) => {
      accMap[path] = get(formApi.getState().values, path);
      return accMap;
    }, {} as PathToFieldValueMap);
    return initVals;
    // This value is only used on mount, so we can ignore the deps array
  }, []);
  const [pathToFieldValueMap, setPathToFieldValueMap] = useState<PathToFieldValueMap>(initialState);
  const pathToFieldMapKeys = Object.keys(pathToFieldValueMap);

  useEffect(() => {
    if (pathToFieldMapKeys.length !== memoizedPaths.length) {
      const paths = memoizedPaths.map((path) => path);
      setPathToFieldValueMap((curr) => pick(curr, paths));
    }
    return () => {};
  }, [
    pathToFieldMapKeys.length,
    memoizedPaths,
  ]);

  useEffect(() => {
    const unsubFunctions = memoizedPaths.map((path) =>
      formApi.registerField(
        path,
        ({ value }) =>
          setPathToFieldValueMap((curr) => ({
            ...curr,
            [path]: value,
          })),
        {
          value: true,
        },
        {
          isEqual: stableCompareFunction,
        },
      ),
    );

    return () => {
      unsubFunctions.forEach((unsubFunc) => unsubFunc());
    };
  }, [
    formApi,
    memoizedPaths,
    stableCompareFunction,
  ]);

  return useMemoCompare(pathToFieldValueMap);
};
